// « Preuve » — the PMS run day by day against Vega (PMS-PROOF-BRIEF §5, §10).
//
// One row per day the proof closed: the PMS's own figures beside Vega's, and
// the écarts under it, each to be classed M · P · V · R. **A day is « propre »
// when every écart is classed**, not when there is none (§5) — so the screen
// counts what is left to class, never « 0 écart » as the goal.
//
// Operator-only: `pms.proof.read` to see, `pms.proof.classify` to class. Both
// are gated again in the RPCs; hiding the tab and the controls is only the
// courtesy of not offering what would be refused (ARCHITECTURE §1).
//
// Classing is ONE call per écart. A subject usually carries several écarts
// (in house and adults together), and classing them in one gesture would be N
// calls that can half-succeed — Hard Rule 2. Each row saves on its own.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { todayIsoParis } from '@lemontaigu/staff';
import type { IsoDate, ProofBackfill, ProofClass, ProofDay, ProofDifference, ProofRange } from '@lemontaigu/pms-core';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import { usePms, usePmsScope } from './PmsProvider.js';
import { fmtDayShort, isoAddDays } from './logic.js';

/** The figures compared, in five views — thirty-one columns do not fit one table. */
export const VIEWS = {
  stays: { label: 'Séjours', metrics: ['in_house', 'arrivals', 'departures', 'adults', 'children', 'infants', 'unresolved', 'groups', 'group_room_nights'] },
  meals: { label: 'Repas', metrics: ['breakfasts', 'dinners', 'packed_lunches'] },
  notes: { label: 'Notes', metrics: ['note_10', 'note_20', 'note_0', 'note_tax', 'tax_due', 'note_unmapped', 'plan_room_night'] },
  payments: {
    label: 'Règlements',
    metrics: ['paid_card', 'paid_amex', 'paid_vad', 'paid_cash', 'paid_cheque', 'paid_ancv', 'paid_transfer', 'paid_booking', 'deposit_held'],
  },
  invoices: { label: 'Factures', metrics: ['inv_10', 'inv_20', 'inv_0', 'inv_other'] },
} as const;
export type ViewKey = keyof typeof VIEWS;
export const METRICS = [
  ...VIEWS.stays.metrics, ...VIEWS.meals.metrics, ...VIEWS.notes.metrics, ...VIEWS.payments.metrics, ...VIEWS.invoices.metrics,
];

/** PR3–PR5: these figures are CENTS — a booking's note, a plan's night, what was paid and held, what was invoiced. */
export const MONEY_METRICS: ReadonlySet<string> = new Set([
  ...VIEWS.notes.metrics, ...VIEWS.payments.metrics, ...VIEWS.invoices.metrics,
]);

/** A figure as the desk reads it: a count, or euros for the money figures. */
export function fmtFigure(metric: string, v: number): string {
  return MONEY_METRICS.has(metric)
    ? (v / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : String(v);
}

export const METRIC_FR: Record<string, string> = {
  // « Occupation », as on the Planning board (operator, 2026-10-12): rooms occupied that night
  in_house: 'Occupation',
  arrivals: 'Arrivées',
  departures: 'Départs',
  adults: 'Adultes',
  children: 'Enfants',
  infants: 'Bébés',
  unresolved: 'Arrivées non résolues',
  groups: 'Groupes',
  group_room_nights: 'Chambres en groupe',
  // PR2: served where recorded, else owed by the plan (PMS) against Vega's EFFECTIFS
  breakfasts: 'Petits-déjeuners',
  dinners: 'Dîners',
  packed_lunches: 'Paniers repas',
  // PR3: each booking's note on its departure, by VAT category (euros), and a plan's room night
  note_10: 'Note TVA 10 %',
  note_20: 'Note TVA 20 %',
  note_0: 'Note 0 % et débours',
  note_tax: 'Note taxe de séjour',
  // PR6a: the levy the reservation's adults × nights owe, against what Vega billed — the PMS shows why it disagrees
  tax_due: 'Taxe de séjour due (adultes × nuits)',
  note_unmapped: 'Note — codes non reconnus',
  plan_room_night: 'Plan — chambres de la nuit',
  // PR4: what each booking paid that day by tender (refunds negative), in all on its departure, and the deposit
  // held at the end of the day by a booking still to leave
  paid_card: 'Réglé CB',
  paid_amex: 'Réglé AMEX',
  paid_vad: 'Réglé VAD',
  paid_cash: 'Réglé espèces',
  paid_cheque: 'Réglé chèque',
  paid_ancv: 'Réglé ANCV',
  paid_transfer: 'Réglé virement',
  paid_booking: 'Réglé au départ',
  deposit_held: 'Arrhes détenues',
  // PR5: each booking's invoices on its departure, by VAT rate (TTC) — the school chain against Vega's invoices
  inv_10: 'Factures TVA 10 %',
  inv_20: 'Factures TVA 20 %',
  inv_0: 'Factures exonéré (taxe de séjour, débours)',
  inv_other: 'Factures autre taux',
};

export const CLASS_FR: Record<ProofClass, string> = {
  M: 'M — modèle : la PMS ne sait pas exprimer ce que Vega a fait',
  P: 'P — correspondance : le seed l’a mal traduit',
  V: 'V — Vega : Vega se contredit lui-même',
  R: 'R — règle : la PMS applique une règle que Vega n’applique pas',
};

const CLASS_SHORT: Record<ProofClass, string> = { M: 'modèle', P: 'correspondance', V: 'Vega', R: 'règle' };

const CLASSES: readonly ProofClass[] = ['M', 'P', 'V', 'R'];

/** The class filter (operator, 2026-10-14): every écart, only the unclassed ones (« — »), or one class. */
export type ClassFilter = 'all' | 'none' | ProofClass;

export function matchesClass(d: Pick<ProofDifference, 'class'>, f: ClassFilter): boolean {
  return f === 'all' ? true : f === 'none' ? d.class == null : d.class === f;
}

/** The replay's floor (PMS-PROOF-BRIEF §11): the window measured before the backfill. */
export const BACKFILL_FROM = '2025-12-01' as IsoDate;

const RANGES = [
  { key: 'week', label: '7 jours', back: 6 },
  { key: 'month', label: '30 jours', back: 29 },
  { key: 'quarter', label: '90 jours', back: 89 },
  { key: 'since', label: 'Depuis le 01/12/2025', back: null },
] as const;
type RangeKey = (typeof RANGES)[number]['key'];

const at = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('fr-FR', {
        timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    : '—';

/** `pms:<uuid>` is a booking Vega does not hold; anything else is a Vega résa. */
export function subjectLabel(subject: string): string {
  return subject.startsWith('pms:') ? `PMS seule · ${subject.slice(4, 12)}` : `Résa ${subject}`;
}

/** Who the booking is for, under its number: the group, the client, the Vega client number
 *  (operator, 2026-10-12: « client Name and ID will help »). Null when nothing is known. */
export function clientLine(d: Pick<ProofDifference, 'clientName' | 'clientRef' | 'groupName'>): string | null {
  const parts = [
    d.groupName ? `Groupe ${d.groupName}` : null,
    d.clientName,
    d.clientRef ? `client ${d.clientRef}` : null,
  ].filter((p): p is string => !!p);
  return parts.length ? parts.join(' · ') : null;
}

/** The two clocks, in one sentence each. Pure, so it is tested alone. */
export function clockLines(range: ProofRange): string[] {
  const lines: string[] = [];
  if (!range.currentDay) {
    lines.push('Cet établissement n’a pas de journée courante : la preuve ne tourne pas ici.');
    return lines;
  }
  lines.push(
    `Journée PMS : ${fmtDayShort(range.currentDay)} · journée Vega : ${
      range.vegaWorkingDay ? fmtDayShort(range.vegaWorkingDay) : '—'
    } (relevée le ${at(range.vegaReadAt)}).`,
  );
  if (range.vegaWorkingDay && range.currentDay < range.vegaWorkingDay) {
    lines.push(
      'Vega est passé à la journée suivante : la PMS la suivra au prochain contrôle (à :10 et :40), une fois le seed repassé.',
    );
  }
  return lines;
}

/** What the last « reconstituer » request says, in one sentence. Pure. */
export function backfillLine(b: ProofBackfill | null): string {
  if (!b) return 'La preuve n’a pas encore été reconstituée sur les journées passées.';
  const since = b.fromDay.split('-').reverse().join('/');
  if (b.status === 'pending') {
    return `Reconstitution depuis le ${since} demandée le ${at(b.requestedAt)} — elle passera au prochain créneau (à :20 et :50).`;
  }
  if (b.status === 'failed') {
    return `La reconstitution du ${at(b.finishedAt)} a échoué : ${b.error ?? 'raison inconnue'}. Rien n’a été appliqué.`;
  }
  return `Reconstituée depuis le ${since} le ${at(b.finishedAt)} : ${b.bookingsSeen ?? '—'} réservations Vega lues, ${
    b.bookingsInserted ?? '—'} ajoutées, ${b.bookingsRefused ?? '—'} refusées · ${b.daysReplayed ?? '—'} journées rejouées.`;
}

export interface PreuveState {
  allowed: boolean;
  canClassify: boolean;
  canReplay: boolean;
  loading: boolean;
  error: string | null;
  range: ProofRange | null;
}

interface ViewProps {
  state: PreuveState;
  rangeKey: RangeKey;
  onRange: (k: RangeKey) => void;
  onClassify: (d: ProofDifference, cls: ProofClass | null, note: string | null) => Promise<string | null>;
  onBackfill: () => Promise<string | null>;
}

export function Preuve() {
  const { proof } = usePms();
  const { current } = usePmsScope();
  const allowed = useCapability(CAPABILITIES.PMS_PROOF_READ);
  const canClassify = useCapability(CAPABILITIES.PMS_PROOF_CLASSIFY);
  const canReplay = useCapability(CAPABILITIES.PMS_PROOF_REPLAY);
  const [rangeKey, setRangeKey] = useState<RangeKey>('month');
  const [range, setRange] = useState<ProofRange | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const window_ = useMemo(() => {
    const r = RANGES.find((x) => x.key === rangeKey) ?? RANGES[1];
    const today = todayIsoParis();
    const from = r.back === null ? BACKFILL_FROM : (isoAddDays(today, -r.back) as IsoDate);
    return { from, to: today as IsoDate };
  }, [rangeKey]);

  const load = useCallback(async () => {
    if (!current || !allowed) return;
    setLoading(true);
    setError(null);
    const res = await proof.read(current, window_.from, window_.to);
    setLoading(false);
    if (!res.ok) {
      setRange(null);
      setError(res.error.message ?? 'lecture impossible');
      return;
    }
    setRange(res.value);
  }, [proof, current, allowed, window_.from, window_.to]);

  useEffect(() => {
    void load();
  }, [load]);

  const classify = useCallback(
    async (d: ProofDifference, cls: ProofClass | null, note: string | null) => {
      const res = await proof.classify(d.id, cls, note);
      if (!res.ok) return res.error.message ?? 'classement refusé';
      await load();
      return null;
    },
    [proof, load],
  );

  const backfill = useCallback(async () => {
    if (!current) return 'aucun établissement choisi';
    const res = await proof.requestBackfill(current, BACKFILL_FROM);
    if (!res.ok) return res.error.message ?? 'demande refusée';
    await load();
    return null;
  }, [proof, current, load]);

  return (
    <PreuveView
      state={{ allowed, canClassify, canReplay, loading, error, range }}
      rangeKey={rangeKey}
      onRange={setRangeKey}
      onClassify={classify}
      onBackfill={backfill}
    />
  );
}

export function PreuveView({ state, rangeKey, onRange, onClassify, onBackfill }: ViewProps) {
  const { allowed, canClassify, canReplay, loading, error, range } = state;
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [opened, setOpened] = useState(false);
  const [onlyDiffs, setOnlyDiffs] = useState(false);
  const [classFilter, setClassFilter] = useState<ClassFilter>('all');
  const [view, setView] = useState<ViewKey>('stays');
  const shown = VIEWS[view].metrics;
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);

  // First load: open every CLOSED day that still has écarts to class. Replayed
  // days stay folded — a season of them would bury the page.
  useEffect(() => {
    if (opened || !range) return;
    setOpen(new Set(range.days.filter((d) => d.unclassified > 0 && d.origin === 'close').map((d) => d.closedDay)));
    setOpened(true);
  }, [range, opened]);

  const ask = async () => {
    setAsking(true);
    setAskError(null);
    const e = await onBackfill();
    setAsking(false);
    if (e) setAskError(e);
  };

  const toggle = (day: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(day)) n.delete(day);
      else n.add(day);
      return n;
    });

  const totals = useMemo(() => {
    const days = range?.days ?? [];
    return {
      days: days.length,
      clean: days.filter((d) => d.clean).length,
      differences: days.reduce((n, d) => n + d.differences.length, 0),
      unclassified: days.reduce((n, d) => n + d.unclassified, 0),
    };
  }, [range]);

  if (!allowed) {
    return (
      <div className="occ">
        <h1>Preuve PMS</h1>
        <p className="muted">Cette page demande l’accès à la preuve PMS (pms.proof.read).</p>
      </div>
    );
  }

  return (
    <div className="occ">
      <header className="occ-head">
        <div>
          <h1>Preuve PMS</h1>
          <p className="muted">
            Chaque journée close dans Vega est refermée ici, et les chiffres que la PMS calcule
            elle-même sont comparés à ceux de Vega. Une journée est <strong>propre</strong> quand
            chaque écart a reçu une classe — pas quand il n’y en a aucun.
          </p>
          {range && clockLines(range).map((l) => (
            <p key={l} className="muted">{l}</p>
          ))}
        </div>
        <div className="occ-ranges">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              className={r.key === rangeKey ? 'is-active' : ''}
              onClick={() => onRange(r.key)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      {error && (
        <p className="occ-error">
          ⚠ {error} — la preuve n’a pas pu être lue : ne pas en conclure qu’il n’y a pas d’écart.
        </p>
      )}
      {loading && !range && <p className="muted">Chargement…</p>}

      {range && (
        <div className="pms-proof-backfill" data-testid="proof-backfill">
          <p className="muted">{backfillLine(range.lastBackfill)}</p>
          {canReplay && range.lastBackfill?.status !== 'pending' && (
            <button type="button" className="pms-chip" disabled={asking} onClick={() => void ask()}>
              {asking ? '…' : range.lastBackfill ? 'Reconstituer à nouveau' : 'Reconstituer depuis le 01/12/2025'}
            </button>
          )}
          {askError && <span className="occ-neg"> ⚠ {askError}</span>}
        </div>
      )}

      {range && (
        <>
          <div className="occ-ranges pms-proof-views" role="tablist">
            {(Object.keys(VIEWS) as ViewKey[]).map((k) => (
              <button key={k} type="button" role="tab" aria-selected={k === view}
                      className={k === view ? 'is-active' : ''} onClick={() => setView(k)}>
                {VIEWS[k].label}
              </button>
            ))}
          </div>
          <label className="muted pms-proof-filter">
            <input type="checkbox" checked={onlyDiffs} onChange={(e) => setOnlyDiffs(e.target.checked)} />{' '}
            Seulement les journées avec écarts
          </label>
          <label className="muted pms-proof-filter">
            Classe :{' '}
            <select value={classFilter} onChange={(e) => setClassFilter(e.target.value as ClassFilter)}>
              <option value="all">Toutes</option>
              <option value="none">— (à classer)</option>
              {CLASSES.map((c) => (
                <option key={c} value={c} title={CLASS_FR[c]}>
                  {c} — {CLASS_SHORT[c]}
                </option>
              ))}
            </select>
          </label>
          <p className="muted" data-testid="proof-summary">
            {totals.days === 0
              ? 'Aucune journée close sur cette période.'
              : `${totals.days} journée${totals.days > 1 ? 's' : ''} close${totals.days > 1 ? 's' : ''} · ${totals.clean} propre${totals.clean > 1 ? 's' : ''} · ${totals.differences} écart${totals.differences > 1 ? 's' : ''}, dont ${totals.unclassified} à classer.`}
          </p>

          {totals.days > 0 && (
            <table className="occ-table pms-proof">
              <thead>
                <tr>
                  <th>Jour</th>
                  {shown.map((m) => (
                    <th key={m} title="PMS / Vega quand ils diffèrent">{METRIC_FR[m]}</th>
                  ))}
                  <th>Écarts</th>
                  <th>État</th>
                </tr>
              </thead>
              <tbody>
                {range.days
                  .filter((d) => !onlyDiffs || d.differences.length > 0)
                  .filter((d) => classFilter === 'all' || d.differences.some((x) => matchesClass(x, classFilter)))
                  .map((d) => (
                  <DayRows
                    key={d.closedDay}
                    day={d}
                    classFilter={classFilter}
                    metrics={shown}
                    open={open.has(d.closedDay)}
                    onToggle={() => toggle(d.closedDay)}
                    canClassify={canClassify}
                    onClassify={onClassify}
                  />
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

function DayRows({
  day,
  classFilter,
  metrics,
  open,
  onToggle,
  canClassify,
  onClassify,
}: {
  day: ProofDay;
  classFilter: ClassFilter;
  metrics: readonly string[];
  open: boolean;
  onToggle: () => void;
  canClassify: boolean;
  onClassify: ViewProps['onClassify'];
}) {
  const span = metrics.length + 3;
  return (
    <>
      <tr className={day.unclassified > 0 ? 'is-differ' : undefined}>
        <th scope="row">
          <button type="button" className="pms-link" onClick={onToggle} aria-expanded={open}>
            {open ? '▾' : '▸'} {fmtDayShort(day.closedDay)}
          </button>
          {day.origin === 'replay' && (
            <span
              className="muted pms-proof-replayed"
              title="Journée rejouée après coup contre l’état actuel de Vega : elle prouve le modèle et la correspondance, pas la clôture du jour."
            >
              reconstitué
            </span>
          )}
          {day.caughtUp && (
            <span
              className="occ-warn"
              title="Comparée après que Vega a avancé de plus d’un jour : les chiffres sont ceux d’un relevé postérieur à la clôture de ce jour."
            >
              {' '}· rattrapage
            </span>
          )}
        </th>
        {metrics.map((m) => {
          const p = day.figures.pms[m] ?? 0;
          const v = day.figures.vega[m] ?? 0;
          return p === v ? (
            <td key={m}>{fmtFigure(m, p)}</td>
          ) : (
            <td key={m} className="occ-differ" title={`PMS ${fmtFigure(m, p)} · Vega ${fmtFigure(m, v)}`}>
              {fmtFigure(m, p)} / {fmtFigure(m, v)}
            </td>
          );
        })}
        <td>{day.differences.length}</td>
        <td>
          {day.clean ? (
            <span className="occ-ok">Propre</span>
          ) : (
            <span className="occ-warn">{day.unclassified} à classer</span>
          )}
        </td>
      </tr>
      {open && (
        <tr className="pms-proof-detail">
          <td colSpan={span}>
            <p className="muted">
              {day.origin === 'replay'
                ? `Rejouée le ${at(day.closedAt)} contre l’état actuel de Vega`
                : `Clôturée le ${at(day.closedAt)} (${day.mode === 'vega' ? 'en suivant Vega' : day.mode === 'manual' ? 'à la main' : day.mode})`}
              {day.vegaImportId != null && ` · relevé Vega n° ${day.vegaImportId}`}
              {day.unresolvedArrivals > 0 && ` · ${day.unresolvedArrivals} arrivée(s) non résolue(s) à la clôture`}
            </p>
            {day.differences.length === 0 ? (
              <p className="occ-ok">Aucun écart : la PMS et Vega disent la même chose pour ce jour.</p>
            ) : (
              <table className="occ-table">
                <thead>
                  <tr>
                    <th>Réservation</th>
                    <th>Chiffre</th>
                    <th>PMS</th>
                    <th>Vega</th>
                    <th>Classe</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {day.differences.filter((x) => matchesClass(x, classFilter)).map((x) => (
                    <DifferenceRow key={x.id} d={x} canClassify={canClassify} onClassify={onClassify} />
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function DifferenceRow({
  d,
  canClassify,
  onClassify,
}: {
  d: ProofDifference;
  canClassify: boolean;
  onClassify: ViewProps['onClassify'];
}) {
  const [cls, setCls] = useState<ProofClass | ''>(d.class ?? '');
  const [note, setNote] = useState(d.note ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A reload brings the saved values back: follow them.
  useEffect(() => {
    setCls(d.class ?? '');
    setNote(d.note ?? '');
  }, [d.class, d.note]);

  const dirty = cls !== (d.class ?? '') || note.trim() !== (d.note ?? '');

  const save = async () => {
    setBusy(true);
    setError(null);
    const e = await onClassify(d, cls === '' ? null : cls, note.trim() === '' ? null : note.trim());
    setBusy(false);
    if (e) setError(e);
  };

  return (
    <tr className={d.class ? undefined : 'is-differ'}>
      <th scope="row">
        {subjectLabel(d.subject)}
        {clientLine(d) && <div className="muted pms-proof-client">{clientLine(d)}</div>}
      </th>
      <td>{METRIC_FR[d.metric] ?? d.metric}</td>
      <td>{fmtFigure(d.metric, d.pms)}</td>
      <td>{fmtFigure(d.metric, d.vega)}</td>
      <td>
        {canClassify ? (
          <select
            aria-label={`Classe de l’écart ${d.id}`}
            value={cls}
            disabled={busy}
            onChange={(e) => setCls(e.target.value as ProofClass | '')}
          >
            <option value="">— à classer</option>
            {CLASSES.map((c) => (
              <option key={c} value={c} title={CLASS_FR[c]}>
                {c} — {CLASS_SHORT[c]}
              </option>
            ))}
          </select>
        ) : d.class ? (
          <span title={CLASS_FR[d.class]}>{d.class}</span>
        ) : (
          <span className="occ-warn">à classer</span>
        )}
      </td>
      <td>
        {canClassify ? (
          <span className="pms-proof-note">
            <input
              type="text"
              aria-label={`Note de l’écart ${d.id}`}
              value={note}
              disabled={busy}
              placeholder="Pourquoi (facultatif)"
              onChange={(e) => setNote(e.target.value)}
            />
            <button type="button" className="pms-chip is-primary" disabled={!dirty || busy} onClick={() => void save()}>
              {busy ? '…' : 'Enregistrer'}
            </button>
            {error && <span className="occ-neg"> ⚠ {error}</span>}
          </span>
        ) : (
          d.note ?? <span className="muted">—</span>
        )}
      </td>
    </tr>
  );
}
